import '../../styles/restScreens.css';
// BL-177 Step 2m: a client's communication choices, read-only for staff except recording an opt-out.
import React, { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { supabaseClient } from '../../config/supabase';
import Button3D from '../ui/Button3D';
import Modal3D from '../ui/Modal3D';
import StatusChip from '../ui/StatusChip';
import { SupportWriteGuard } from '../../context/SupportSessionContext';
import { normalizeCommunicationPreferences, smsOptOutMessage } from '../../lib/smsConsent';
import { timeZoneLabel } from '../../lib/contactTimeZone';

export const REASON_MIN = 3;
export const REASON_MAX = 300;

// Same alerts, titles and wording as the client's own screen.
const ALERTS = [
    ['reservation_confirmations', 'Reservation Confirmations', 'Notification of confirmation'],
    ['upcoming_trip_details_24hrs', 'Upcoming Trip Details (24hrs)', 'Driver, vehicle, pickup time the day before'],
    ['driver_vehicle_location', 'Driver/Vehicle Location', 'Driver/vehicle arrival at your door'],
    ['thank_you', 'Thank You!', 'Trip complete and invoice/receipt'],
    ['post_trip_reminder', 'Rating reminder', 'A separate reminder to rate your completed trip'],
];

const THEMES = {
    light: { ink: 'text-[var(--ink)]', muted: 'text-[var(--ink-muted)]', line: 'border-[var(--surface-border)]' },
    dark: { ink: 'text-white', muted: 'text-slate-300', line: 'border-[#3B4D73]' },
};

export function formatDay(value) {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

/** The "who set these" badge: words, never colour alone. */
export function preferencesBadge(contact) {
    const day = formatDay(contact?.communication_preferences_set_at);
    const suffix = day ? ` · ${day}` : '';
    if (contact?.communication_preferences_set_by === 'client') return { tone: 'slate', label: `Set by client${suffix}`, key: 'client' };
    if (contact?.communication_preferences_set_by === 'support_admin') return { tone: 'orange', label: `Changed by support admin${suffix}`, key: 'support_admin' };
    return { tone: 'grey', label: 'Not set by the client yet', key: 'none' };
}

export function reasonProblem(reason) {
    const text = String(reason ?? '').trim();
    if (text.length < REASON_MIN) return `Add a short reason (at least ${REASON_MIN} characters).`;
    return '';
}

// What the screen shows: the client's saved choices, with any opt-out already applied (a text opt-out turns every text option off,
// an email opt-out turns every email option off), so a stale row can never read "On" for a channel the client opted out of.
function shownPreferences(contact) {
    const prefs = normalizeCommunicationPreferences(contact?.communication_preferences, contact || {});
    if (contact?.email_opted_out_at) {
        prefs.trip_updates.email = false;
        prefs.offers_news.by_email = false;
    }
    return prefs;
}

function optOutSentence(contact) {
    if (!contact?.sms_opted_out_at) return '';
    return contact.sms_consent_source === 'staff_opt_out'
        ? 'Texts are off for this customer. A staff member recorded the opt-out.'
        : smsOptOutMessage(contact.sms_opted_out_at);
}

// BL-177 Step 2j's platform popup (Modal3D) wraps the form. The submit button sits in the popup's own action row and points at the form.
// Rendered through a portal: a card underneath must not react to clicks made inside the dialog.
function DialogShell({ title, subtitle, label, busy = false, onSubmit, onClose, submitLabel, submitDisabled = false, children, testId = 'contact-communications-dialog' }) {
    const formId = useId();
    const stop = (event) => event.stopPropagation();
    return createPortal(
        <div onClick={stop} onDoubleClick={stop} onPointerDown={stop} onKeyDown={stop}>
            <Modal3D
                title={title}
                subtitle={subtitle}
                testId={testId}
                aria-label={label || title}
                data-contact-communications-dialog="true"
                dismissible={!busy}
                onClose={onClose}
                actions={(
                    <>
                        <Button3D type="button" onClick={onClose} disabled={busy}>Cancel</Button3D>
                        <Button3D type="submit" form={formId} variant="go" disabled={submitDisabled || busy}>{busy ? 'Saving…' : submitLabel}</Button3D>
                    </>
                )}
            >
                <form id={formId} onSubmit={onSubmit} className="flex flex-col gap-3">{children}</form>
            </Modal3D>
        </div>,
        document.body
    );
}

function ReasonField({ id, value, onChange, touched, inputRef }) {
    const problem = reasonProblem(value);
    return (
        <label htmlFor={id} className="flex flex-col gap-1 text-sm font-bold">
            Why? <span className="font-normal">(required, a few words)</span>
            <input id={id} ref={inputRef} type="text" value={value} maxLength={REASON_MAX}
                onChange={(event) => onChange(event.target.value.slice(0, REASON_MAX))}
                aria-required="true" aria-invalid={touched && problem ? 'true' : 'false'}
                className="w-full rounded-lg border border-[#3B4D73] bg-[#0B1220] p-2 text-sm font-normal text-white" />
            {touched && problem && <span role="alert" className="text-sm font-normal text-[#FCA5A5]">{problem}</span>}
        </label>
    );
}

// ---- Record opt-out: texts or email, switch OFF only ----------------------------------------------------------------------
function OptOutDialog({ contact, view, onDone, onClose }) {
    const id = useId();
    const [channel, setChannel] = useState('');
    const [reason, setReason] = useState('');
    const [touched, setTouched] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const reasonRef = useRef(null);
    const textOff = Boolean(view.sms_opted_out_at);
    const emailOff = Boolean(view.email_opted_out_at);
    const ready = Boolean(channel) && !reasonProblem(reason);

    const submit = async (event) => {
        event.preventDefault();
        setTouched(true);
        if (!ready || busy) return;
        setBusy(true);
        setError('');
        try {
            const { error: rpcError } = await supabaseClient.rpc('comms_record_opt_out', {
                p_contact_id: contact.id, p_channel: channel, p_reason: reason.trim(),
            });
            if (rpcError) throw rpcError;
            onDone(channel);
        } catch (err) {
            setError(err?.message || 'Could not record the opt-out.');
            setBusy(false);
        }
    };

    return (
        <DialogShell title="Record an opt-out" subtitle="The client does not want texts or email from us." label="Record an opt-out" submitLabel="Save opt-out" submitDisabled={!ready} busy={busy} onSubmit={submit} onClose={onClose}>
            <p className="text-sm">Use this when the client tells you they do not want texts or email from us. It switches that channel off right away.</p>
            <p className="rounded-lg border border-[#3B4D73] bg-[#0B1220] p-2 text-sm" data-optout-rule="true">
                Staff can only switch a channel off. We can never switch it back on: only the client can, in their own app
                {' '}(for texts, by replying START).
            </p>
            <div role="group" aria-label="Which channel" className="flex flex-col gap-1">
                <span className="text-sm font-bold">Which channel?</span>
                <div className="flex gap-2">
                    {[['text', 'Texts', textOff], ['email', 'Email', emailOff]].map(([key, text, already]) => (
                        <Button3D key={key} variant={channel === key ? 'go' : 'light'} aria-pressed={channel === key}
                            disabled={busy || already} onClick={() => { setChannel(key); reasonRef.current?.focus(); }}>
                            {text}{already ? ' (already off)' : ''}
                        </Button3D>
                    ))}
                </div>
            </div>
            <ReasonField id={`${id}-reason`} value={reason} onChange={setReason} touched={touched} inputRef={reasonRef} />
            {error && <p role="alert" className="text-sm text-[#FCA5A5]">{error}</p>}
        </DialogShell>
    );
}

// One read-only line: the words, then On / Off (a word, never colour alone). No input, nothing to click.
function PrefRow({ prefKey, on, ink, nested = false, children }) {
    return (
        <li data-pref={prefKey} data-state={on ? 'on' : 'off'} className={`flex items-start justify-between gap-3 text-xs ${ink} ${nested ? 'ml-4' : ''}`}>
            <span className="min-w-0">{children}</span>
            <StatusChip tone={on ? 'green' : 'grey'}>{on ? 'On' : 'Off'}</StatusChip>
        </li>
    );
}

// ---- The read-only block ----------------------------------------------------------------------------------------------------
export default function ContactCommunications({
    contact, theme = 'light', showActions = true, onChanged, heading = 'Communication preferences', idPrefix = 'contact-comms',
}) {
    const t = THEMES[theme === 'dark' ? 'dark' : 'light'];
    const [patch, setPatch] = useState({});          // what just changed on this screen, until the parent reloads the contact
    const [dialog, setDialog] = useState('');
    const [message, setMessage] = useState('');
    const signature = JSON.stringify([contact?.id, contact?.communication_preferences, contact?.communication_preferences_set_by,
        contact?.communication_preferences_set_at, contact?.sms_opted_out_at, contact?.email_opted_out_at]);
    useEffect(() => { setPatch({}); setMessage(''); }, [signature]);

    if (!contact) return null;
    const view = { ...contact, ...patch };
    const prefs = shownPreferences(view);
    const badge = preferencesBadge(view);
    const textOptOut = optOutSentence(view);
    const canAct = showActions && Boolean(contact.id);

    const Row = (props) => <PrefRow ink={t.ink} {...props} />;

    const finish = (changes, text, kind) => {
        setPatch((current) => ({ ...current, ...changes }));
        setDialog('');
        setMessage(text);
        onChanged?.({ kind, contactId: contact.id, changes });
    };

    return (
        <div data-m6-layout="clients" data-m6-component="ContactCommunications" role="group" aria-label={heading} data-contact-communications="true" className="space-y-3">
            <h3 className={`text-sm font-semibold ${t.ink}`}>{heading}</h3>
            <div className="flex flex-wrap items-center gap-2">
                <StatusChip tone={badge.tone} title={badge.key === 'none' ? 'The client has not chosen yet, so the standard defaults apply.' : undefined}>
                    <span data-comms-badge={badge.key}>{badge.label}</span>
                </StatusChip>
                {view.sms_opted_out_at && <StatusChip tone="red"><span data-opted-out="text">Opted out of texts · {formatDay(view.sms_opted_out_at)}</span></StatusChip>}
                {view.email_opted_out_at && <StatusChip tone="red"><span data-opted-out="email">Opted out of email · {formatDay(view.email_opted_out_at)}</span></StatusChip>}
            </div>
            <p className={`text-xs ${t.muted}`}>These are the client&apos;s own choices. You can see them and switch a channel off for them, but only they can switch one on.</p>
            {textOptOut && <p className="text-xs text-red-700 dark:text-red-300" data-text-opt-out-note="true">{textOptOut}</p>}

            <section className="space-y-2" aria-label="Trip updates">
                <h4 className={`text-xs font-semibold ${t.ink}`}>Trip updates</h4>
                <ul className="space-y-2">
                    <Row prefKey="trip_updates.enabled" on={prefs.trip_updates.enabled}>Trip updates</Row>
                    <Row prefKey="trip_updates.email" on={prefs.trip_updates.email}>Email — Confirmations and receipts by email</Row>
                    <Row prefKey="trip_updates.text" on={prefs.trip_updates.text}>Text messages — Trip alerts to your mobile</Row>
                </ul>
                <div className={`ml-4 space-y-2 border-l-2 pl-3 ${t.line}`}>
                    <p className={`text-[11px] font-semibold ${t.ink}`}>Trip alerts</p>
                    <ul className="space-y-2">
                        {ALERTS.map(([key, title, description]) => (
                            <Row key={key} prefKey={`trip_updates.alerts.${key}`} on={prefs.trip_updates.alerts[key]}>
                                <strong>{title}</strong> — {description}
                            </Row>
                        ))}
                    </ul>
                </div>
            </section>

            <section className="space-y-2" aria-label="Offers and news">
                <h4 className={`text-xs font-semibold ${t.ink}`}>Offers &amp; news</h4>
                <ul className="space-y-2">
                    <Row prefKey="offers_news.enabled" on={prefs.offers_news.enabled}><strong>Offers &amp; news</strong></Row>
                    <Row prefKey="offers_news.by_text" on={prefs.offers_news.by_text} nested>By text</Row>
                    <Row prefKey="offers_news.by_email" on={prefs.offers_news.by_email} nested>By email</Row>
                </ul>
            </section>

            {view.time_zone && (
                <p className={`text-xs ${t.ink}`} data-contact-time-zone="true">
                    <span className="font-semibold">Time zone</span> — {timeZoneLabel(view.time_zone) || view.time_zone}{view.time_zone_source === 'manual' ? ' (chosen by a person)' : ' (suggested)'}
                </p>
            )}

            {message && <p className={`text-xs ${t.ink}`} role="status">{message}</p>}

            {canAct && (
                <div className="flex flex-wrap gap-2">
                    <SupportWriteGuard mode="disable" tooltip="Recording an opt-out is disabled in a read-only support session">
                        <Button3D variant="light" id={`${idPrefix}-record-opt-out`} onClick={() => setDialog('optout')}>Record opt-out…</Button3D>
                    </SupportWriteGuard>
                </div>
            )}

            {dialog === 'optout' && (
                <OptOutDialog contact={contact} view={view} onClose={() => setDialog('')}
                    onDone={(channel) => {
                        const stamp = new Date().toISOString();
                        finish(channel === 'text' ? { sms_opted_out_at: stamp, sms_consent_source: 'staff_opt_out' } : { email_opted_out_at: stamp },
                            channel === 'text' ? 'Recorded. Texts are now off for this client.' : 'Recorded. Email is now off for this client.', 'opt_out');
                    }} />
            )}
        </div>
    );
}
